import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography,
} from '@mui/material';
import { ArchitectureOutlined as PlanIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { ProjetDessin } from '../../types/projetDessin';
import { extractErrorMessage } from '../../utils/errorHandler';
import PlanViewerDialog from './PlanViewerDialog';
import { PlanKindChip, planMeta } from './planUi';

/** « Plans (n) » on an operator's task (Comptoir étape, Mon Suivi): one plan opens straight in
 *  the viewer, several open a list with the technical notes. */
const PlansButton: React.FC<{ count?: number; title: string; load: () => Promise<ProjetDessin[]>; color?: string }> = ({ count, title, load, color }) => {
  const [plans, setPlans] = useState<ProjetDessin[] | null>(null);
  const [vue, setVue] = useState<ProjetDessin | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!count) return null;
  const open = async () => {
    setBusy(true); setErr('');
    try {
      const list = await load();
      if (list.length === 1) setVue(list[0]); else setPlans(list);
    } catch (e: any) { setErr(extractErrorMessage(e)); setPlans([]); } finally { setBusy(false); }
  };
  const notes = (plans || []).filter((p) => p.observations);
  return (
    <>
      <Button size="small" variant="outlined" onClick={open} disabled={busy}
        startIcon={busy ? <CircularProgress size={14} /> : <PlanIcon />}
        sx={color ? { borderColor: color, color } : undefined}>
        Plans ({count})
      </Button>
      <Dialog open={plans !== null} onClose={() => setPlans(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Plans · {title}</DialogTitle>
        <DialogContent dividers>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          {plans && !plans.length && !err && <Typography color="text.secondary">Aucun plan pour cette tâche.</Typography>}
          <Stack spacing={1}>
            {(plans || []).map((p) => (
              <Paper key={p.id} variant="outlined" sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => setVue(p)}>
                <PlanKindChip kind={p.kind} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.title}{p.piece_ref ? ` · ${p.piece_ref}` : ''}</Typography>
                  <Typography variant="caption" color="text.secondary">{planMeta(p) || p.latest_version?.file_name}</Typography>
                </Box>
                <Button size="small" startIcon={<ViewIcon />}>Voir le plan</Button>
              </Paper>
            ))}
          </Stack>
          {notes.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <b>Notes techniques :</b> {notes.map((p) => (notes.length > 1 ? `${p.title} : ${p.observations}` : p.observations)).join(' · ')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setPlans(null)}>Fermer</Button></DialogActions>
      </Dialog>
      <PlanViewerDialog plan={vue} onClose={() => setVue(null)} />
    </>
  );
};

export default PlansButton;
